import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, ArrowRight, Loader2 } from 'lucide-react';
import { type Car as CarType } from "../Listings";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { useLanguage } from '../../contexts/LanguageContext';
import { apiService, CarListing as ApiCarListing } from '../../services/api';

interface FeaturedCarsSectionProps {
  onCarSelect: (car: CarType) => void;
  onCarBuy: (car: CarType) => void;
}

const KNOWN_VEHICLE_TYPES = new Set([
  'sedan', 'suv', 'hatchback', 'truck', 'coupe', 'convertible', 'wagon', 'van', 'minivan', 'crossover',
]);

function getDisplayType(apiCar: ApiCarListing): string {
  const rawType =
    apiCar.vehicle_type == null
      ? ''
      : String(apiCar.vehicle_type).trim();
  if (rawType && KNOWN_VEHICLE_TYPES.has(rawType.toLowerCase())) {
    return rawType;
  }
  const make =
    apiCar.make == null ? '' : String(apiCar.make).trim();
  return make || rawType || 'Sedan';
}

function locationPartsFromApi(apiCar: ApiCarListing): string[] {
  const loc = apiCar.location;
  if (typeof loc === 'string' && loc.trim()) {
    return loc.split(',').map((p) => p.trim()).filter(Boolean);
  }
  return [];
}

export function FeaturedCarsSection({ onCarSelect, onCarBuy }: FeaturedCarsSectionProps) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [featuredCars, setFeaturedCars] = useState<CarType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedCars = async () => {
      try {
        const response = await apiService.getCarListings({ limit: 2 });
        
        // Transform API car listings to our Car interface (use real pricing and location)
        const transformedCars: CarType[] = (response.data || []).map((apiCar: ApiCarListing) => {
          const parts = locationPartsFromApi(apiCar);
          const city = parts[0] || apiCar.dealer_city || 'Unknown';
          const stateProvince = parts[1] || apiCar.dealer_state || 'Unknown';
          const country = parts[2] || (stateProvince === 'Quebec' || stateProvince === 'Ontario' ? 'Canada' : 'USA');
          return {
            id: apiCar.id,
            make: apiCar.make,
            model: apiCar.model,
            year: apiCar.year,
            monthlyPayment: apiCar.monthly_payment ?? Math.round(apiCar.price / 60),
            termMonths: apiCar.term_months ?? 60,
            minCreditScore: apiCar.min_credit_score ?? 650,
            type: getDisplayType(apiCar),
            dealer: apiCar.dealer_name || 'Unknown Dealer',
            mileage: apiCar.mileage,
            transmission: apiCar.transmission,
            fuelType: apiCar.fuel_type,
            totalPrice: apiCar.price,
            downPayment: apiCar.down_payment,
            description: apiCar.description,
            images: apiCar.images || [],
            location: { country, stateProvince, city }
          };
        });
        
        setFeaturedCars(transformedCars);
      } catch (error) {
        console.error('Failed to fetch featured cars:', error);
        // Fallback to empty array if API fails
        setFeaturedCars([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedCars();
  }, []);

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('featured.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('featured.subtitle')}
            </p>
          </div>
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-12 w-12 text-gray-500 animate-spin" />
            <p className="ml-4 text-gray-500">Loading featured cars...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('featured.title')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('featured.subtitle')}
          </p>
        </div>

        {featuredCars.length > 0 ? (
          <>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {featuredCars.map((car, index) => (
                <Card 
                  key={car.id} 
                  className="group cursor-pointer hover:shadow-xl transition-all duration-300 overflow-hidden"
                  onClick={() => onCarSelect(car)}
                >
                  <div className="relative">
                    <div className="h-48 bg-gray-100 overflow-hidden">
                      {car.images && car.images.length > 0 ? (
                        <img
                          src={car.images[0]}
                          alt={`${car.year} ${car.make} ${car.model}`}
                          className="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                          onError={(e) => {
                            // Fallback to placeholder on error
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const placeholder = target.nextElementSibling as HTMLDivElement;
                            if (placeholder) placeholder.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      {/* Error fallback placeholder (shown when no image or on error) */}
                      <div 
                        className="h-full bg-gradient-to-br from-[#f91bdc]/10 to-[#f91bdc]/20 flex items-center justify-center"
                        style={{ display: car.images && car.images.length > 0 ? 'none' : 'flex' }}
                      >
                        <Car size={64} className="text-[#f91bdc]/60" />
                      </div>
                    </div>
                    
                    <div className="absolute top-4 right-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${
                        index === 0 ? 'bg-green-500' : 'bg-blue-500'
                      }`}>
                        {index === 0 ? t('featured.bestValue') : t('featured.popularChoice')}
                      </span>
                    </div>
                  </div>

                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-1">
                          {car.year} {car.make} {car.model}
                        </h3>
                        <p className="text-gray-600">{car.type} • {car.dealer}</p>
                        <p className="text-sm text-gray-500">{t('car.creditScore')}: {car.minCreditScore}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-[#f91bdc]">
                          ${car.monthlyPayment}/mo
                        </div>
                        <p className="text-sm text-gray-500">{car.termMonths} {t('car.months')}</p>
                      </div>
                    </div>

                    <div className="flex space-x-3">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCarSelect(car);
                        }}
                      >
                        {t('featured.seeDetails')}
                      </Button>
                      <Button 
                        className="flex-1 bg-[#f91bdc] hover:bg-[#e015c9]"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCarBuy(car);
                        }}
                      >
                        {t('common.buyNow')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button 
                size="lg"
                onClick={() => navigate('/listings')}
                className="bg-[#f91bdc] hover:bg-[#e015c9] text-white px-8 py-3 text-lg"
              >
                {t('featured.browseAllCars')} <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <Car size={64} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No featured cars available
            </h3>
            <p className="text-gray-600 mb-6">
              Check back later for our latest featured vehicles.
            </p>
            <Button 
              onClick={() => navigate('/listings')}
              className="bg-[#f91bdc] hover:bg-[#e015c9]"
            >
              Browse All Cars
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}